import {
  arrayOf,
  boolOf,
  fail,
  idOf,
  numberOf,
  objectOf,
  optionalBool,
  optionalNumber,
  optionalText,
  requireRid,
  ridOf,
  textList,
  textOf,
  type WireObject,
} from '../../../shared/api/wire'
import { decodeConnection } from '../../../shared/api/connection'
import type {
  AqsiCashiers,
  AqsiDevices,
  AqsiFiscalCheck,
  AqsiGood,
  AqsiGoodsPage,
  AqsiProbeStatus,
  AqsiFreshness,
  AqsiFreshnessCheck,
  AqsiMaterialization,
  AqsiRefreshResult,
  AqsiTaxSummary,
} from '../model/types'

/** Ответы C2 без конверта: rid лежит в самом ответе и сверяется с запрошенным (R07). */
function ofResource(raw: unknown, resourceId: string, field: string): WireObject {
  const data = objectOf(raw, field)
  requireRid(ridOf(data.resourceId, `${field}.resourceId`), resourceId, field)
  return data
}

/**
 * Цена — `numeric(18,2)` службы. В JSON приходит числом; клиент хранит её
 * строкой с двумя знаками (без арифметики с плавающей точкой) только для показа.
 */
function priceOf(value: unknown, field: string): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'string' && /^-?\d+(\.\d{1,2})?$/.test(value)) return value
  if (typeof value === 'number' && Number.isFinite(value)) {
    const text = String(value)
    return /^-?\d+(\.\d{1,2})?$/.test(text) ? text : fail(`${field} — больше двух знаков`)
  }
  return fail(`${field} — не цена`)
}

function decodeMaterialization(raw: unknown): AqsiMaterialization | null {
  if (raw === null || raw === undefined) return null
  const item = objectOf(raw, 'materialization')
  return {
    configurationVersion: textOf(item.configurationVersion, 'materialization.configurationVersion'),
    completedAt: textOf(item.completedAt, 'materialization.completedAt'),
    count: numberOf(item.count, 'materialization.count'),
  }
}

function decodeGood(raw: unknown, resourceId: string, field: string): AqsiGood {
  const item = ofResource(raw, resourceId, field)
  return {
    goodId: idOf(item.goodId, `${field}.goodId`),
    name: textOf(item.name, `${field}.name`),
    sku: optionalText(item.sku, `${field}.sku`),
    price: priceOf(item.price, `${field}.price`),
    barcodes: textList(item.barcodes ?? [], `${field}.barcodes`),
    tax: optionalNumber(item.tax, `${field}.tax`),
    subject: optionalNumber(item.subject, `${field}.subject`),
    unit: optionalText(item.unit, `${field}.unit`),
    unitCode: optionalNumber(item.unitCode, `${field}.unitCode`),
    isWeight: optionalBool(item.isWeight, `${field}.isWeight`),
    revision: numberOf(item.revision, `${field}.revision`),
    observedAt: textOf(item.observedAt, `${field}.observedAt`),
    missingSince: optionalText(item.missingSince, `${field}.missingSince`),
    presence: textOf(item.presence, `${field}.presence`),
    invalidReason: optionalText(item.invalidReason, `${field}.invalidReason`),
  }
}

export function decodeGoodsPage(resourceId: string) {
  return (raw: unknown): AqsiGoodsPage => {
    const data = ofResource(raw, resourceId, 'каталог')
    return {
      page: numberOf(data.page, 'page'),
      pageSize: numberOf(data.pageSize, 'pageSize'),
      total: numberOf(data.total, 'total'),
      materialization: decodeMaterialization(data.materialization),
      items: arrayOf(data.items, 'items').map((item, index) => decodeGood(item, resourceId, `items[${index}]`)),
    }
  }
}

export function decodeProbeStatus(resourceId: string) {
  return (raw: unknown): AqsiProbeStatus => {
    const data = ofResource(raw, resourceId, 'индекс поиска')
    const state = textOf(data.state, 'state')
    if (state !== 'none' && state !== 'building' && state !== 'ready' && state !== 'failed') fail(`state — неизвестное состояние ${state}`)
    return {
      state,
      builtAt: optionalText(data.builtAt, 'builtAt'),
      snapshotAt: optionalText(data.snapshotAt, 'snapshotAt'),
      latestSnapshotAt: optionalText(data.latestSnapshotAt, 'latestSnapshotAt'),
      count: numberOf(data.count, 'count'),
      error: optionalText(data.error, 'error'),
      pending: numberOf(data.pending, 'pending'),
    }
  }
}

const OUTCOMES = ['never', 'unchanged', 'changed', 'full', 'failed'] as const
const RUN_STATUSES = ['done', 'already-running', 'throttled', 'failed'] as const

function decodeFreshnessData(data: WireObject): AqsiFreshness {
  const outcome = textOf(data.outcome, 'outcome')
  if (!(OUTCOMES as readonly string[]).includes(outcome)) fail(`outcome - неизвестный итог ${outcome}`)
  return {
    products: numberOf(data.products, 'products'),
    snapshotAt: optionalText(data.snapshotAt, 'snapshotAt'),
    lastCheckedAt: optionalText(data.lastCheckedAt, 'lastCheckedAt'),
    outcome: outcome as AqsiFreshness['outcome'],
    fullReason: optionalText(data.fullReason, 'fullReason'),
    added: numberOf(data.added, 'added'),
    changed: numberOf(data.changed, 'changed'),
    missing: numberOf(data.missing, 'missing'),
    running: boolOf(data.running, 'running'),
    nextCheckAt: optionalText(data.nextCheckAt, 'nextCheckAt'),
    intervalMinutes: numberOf(data.intervalMinutes, 'intervalMinutes'),
    online: boolOf(data.online, 'online'),
    errorCode: optionalText(data.errorCode, 'errorCode'),
    errorMessage: optionalText(data.errorMessage, 'errorMessage'),
  }
}

export function decodeFreshness(resourceId: string) {
  return (raw: unknown): AqsiFreshness => decodeFreshnessData(ofResource(raw, resourceId, 'свежесть каталога'))
}

export function decodeTaxSummary(resourceId: string) {
  return (raw: unknown): AqsiTaxSummary => {
    const data = ofResource(raw, resourceId, 'сводка НДС кабинета')
    const available = boolOf(data.available, 'available')
    const total = optionalNumber(data.total, 'total')
    const withTax = optionalNumber(data.withTax, 'withTax')
    const withoutTax = optionalNumber(data.withoutTax, 'withoutTax')
    // Данные есть - числа обязаны быть: иначе экран показал бы нули вместо «нет данных».
    if (available && (total === null || withTax === null || withoutTax === null)) fail('сводка НДС кабинета: данные есть, а чисел нет')
    return {
      available,
      reason: optionalText(data.reason, 'reason'),
      total,
      withTax,
      withoutTax,
      byTax: arrayOf(data.byTax, 'byTax').map((entry, index) => {
        const item = objectOf(entry, `byTax[${index}]`)
        return { tax: numberOf(item.tax, `byTax[${index}].tax`), count: numberOf(item.count, `byTax[${index}].count`) }
      }),
      snapshotAt: optionalText(data.snapshotAt, 'snapshotAt'),
    }
  }
}

export function decodeFreshnessCheck(resourceId: string) {
  return (raw: unknown): AqsiFreshnessCheck => {
    const data = objectOf(raw, 'проверка актуальности')
    const status = textOf(data.status, 'status')
    if (!(RUN_STATUSES as readonly string[]).includes(status)) fail(`status - неизвестный итог ${status}`)
    return {
      status: status as AqsiFreshnessCheck['status'],
      retryAfter: optionalText(data.retryAfter, 'retryAfter'),
      freshness: decodeFreshnessData(ofResource(data.freshness, resourceId, 'свежесть каталога')),
    }
  }
}

export function decodeDevices(resourceId: string) {
  return (raw: unknown): AqsiDevices => {
    const data = ofResource(raw, resourceId, 'кассы')
    return {
      materialization: decodeMaterialization(data.materialization),
      items: arrayOf(data.items, 'items').map((entry, index) => {
        const item = ofResource(entry, resourceId, `items[${index}]`)
        return {
          deviceId: numberOf(item.deviceId, `items[${index}].deviceId`),
          serialNumber: textOf(item.serialNumber, `items[${index}].serialNumber`),
          model: optionalText(item.model, `items[${index}].model`),
          fiscalStorageNumber: optionalText(item.fiscalStorageNumber, `items[${index}].fiscalStorageNumber`),
          shopId: optionalText(item.shopId, `items[${index}].shopId`),
          shopName: optionalText(item.shopName, `items[${index}].shopName`),
          receiptTaxSystem: optionalNumber(item.receiptTaxSystem, `items[${index}].receiptTaxSystem`),
          taxSystemSource: textOf(item.taxSystemSource, `items[${index}].taxSystemSource`),
          observedAt: textOf(item.observedAt, `items[${index}].observedAt`),
          missingSince: optionalText(item.missingSince, `items[${index}].missingSince`),
          invalidReason: optionalText(item.invalidReason, `items[${index}].invalidReason`),
          lastContactAt: optionalText(item.lastContactAt, `items[${index}].lastContactAt`),
          connection: decodeConnection(item.connection, `items[${index}].connection`),
        }
      }),
    }
  }
}

export function decodeCashiers(resourceId: string) {
  return (raw: unknown): AqsiCashiers => {
    const data = ofResource(raw, resourceId, 'кассиры')
    return {
      materialization: decodeMaterialization(data.materialization),
      // ИНН кассира служба наружу не отдаёт; декодер его и не читает.
      items: arrayOf(data.items, 'items').map((entry, index) => {
        const item = ofResource(entry, resourceId, `items[${index}]`)
        return {
          cashierId: idOf(item.cashierId, `items[${index}].cashierId`),
          name: textOf(item.name, `items[${index}].name`),
          observedAt: textOf(item.observedAt, `items[${index}].observedAt`),
          missingSince: optionalText(item.missingSince, `items[${index}].missingSince`),
        }
      }),
    }
  }
}

/** Итог «задать цену»: `applied` — кабинет принял, чтение подтвердило; `unchanged` — там уже стояла эта цена. */
export function decodeSetPrice(raw: unknown): { outcome: 'applied' | 'unchanged'; price: number } {
  const data = objectOf(raw, 'цена')
  const outcome = textOf(data.outcome, 'outcome')
  if (outcome !== 'applied' && outcome !== 'unchanged') return fail(`outcome — неизвестное значение «${outcome}»`)
  return { outcome, price: numberOf(data.price, 'price') }
}

/** Цены карточки кабинета живьём (RP-FB39): цена товара и магазины; цены магазина и минимальной цены API не отдаёт. */
export function decodeGoodPrices(raw: unknown): {
  goodId: string
  name: string | null
  price: number | null
  shops: readonly { shopId: string; name: string | null; resourceShop: boolean }[]
  resourceShopId: string
} {
  const data = objectOf(raw, 'цены товара')
  const price = data.price
  return {
    goodId: textOf(data.goodId, 'goodId'),
    name: optionalText(data.name, 'name'),
    price: price === null || price === undefined ? null : numberOf(price, 'price'),
    shops: arrayOf(data.shops, 'shops').map((item, index) => {
      const shop = objectOf(item, `shops[${index}]`)
      return {
        shopId: textOf(shop.shopId, `shops[${index}].shopId`),
        name: optionalText(shop.name, `shops[${index}].name`),
        resourceShop: boolOf(shop.resourceShop, `shops[${index}].resourceShop`),
      }
    }),
    resourceShopId: textOf(data.resourceShopId, 'resourceShopId'),
  }
}

/** Итог цены магазина: кабинет принял; сама цена магазина не подтверждается (`confirmed` всегда false). */
export function decodeSetShopPrice(raw: unknown): { outcome: 'accepted'; shopId: string; price: number; confirmed: boolean } {
  const data = objectOf(raw, 'цена магазина')
  const outcome = textOf(data.outcome, 'outcome')
  if (outcome !== 'accepted') return fail(`outcome — неизвестное значение «${outcome}»`)
  return { outcome, shopId: textOf(data.shopId, 'shopId'), price: numberOf(data.price, 'price'), confirmed: boolOf(data.confirmed, 'confirmed') }
}

export function decodeRefresh(resourceId: string) {
  return (raw: unknown): AqsiRefreshResult => {
    const data = ofResource(raw, resourceId, 'обновление')
    return {
      scope: textOf(data.scope, 'scope'),
      completedAt: textOf(data.completedAt, 'completedAt'),
      seen: numberOf(data.seen, 'seen'),
      added: numberOf(data.added, 'added'),
      changed: numberOf(data.changed, 'changed'),
      newlyMissing: numberOf(data.newlyMissing, 'newlyMissing'),
      invalid: numberOf(data.invalid, 'invalid'),
      unkeyed: numberOf(data.unkeyed, 'unkeyed'),
    }
  }
}

export function decodeFiscalCheck(resourceId: string) {
  return (raw: unknown): AqsiFiscalCheck => {
    const data = ofResource(raw, resourceId, 'проверка')
    const profile = data.profile === null || data.profile === undefined ? null : objectOf(data.profile, 'profile')
    return {
      ready: boolOf(data.ready, 'ready'),
      blocks: arrayOf(data.blocks, 'blocks').map((entry, index) => {
        const item = objectOf(entry, `blocks[${index}]`)
        return {
          code: textOf(item.code, `blocks[${index}].code`),
          message: textOf(item.message, `blocks[${index}].message`),
          goodId: optionalText(item.goodId, `blocks[${index}].goodId`),
        }
      }),
      profile:
        profile === null
          ? null
          : {
              taxSystem: numberOf(objectOf(profile.device, 'profile.device').taxSystem, 'profile.device.taxSystem'),
              legalEntityInn: textOf(profile.legalEntityInn, 'profile.legalEntityInn'),
              checkedAt: textOf(profile.checkedAt, 'profile.checkedAt'),
              lines: arrayOf(profile.lines, 'profile.lines').length,
            },
    }
  }
}
